import React, { useRef } from 'react';
import { observer } from 'mobx-react';
import classnames from 'classnames';

import { UiButton, UiIcon, UiNavLink, UiSwitch } from 'shared/uikit-new';
import { COLORS, ROUTES, SEARCH_TYPES } from 'shared/constants';
import { useDebounce, useNavigate, useOnClickOutside, useStore } from 'shared/hooks';
import { SearchIndexModel } from 'shared/models';
import { searchHintsQuery } from 'shared/queries';

import './styles.scss';

export const LayoutHeaderSearch = observer(() => {
    const store = useStore(() => ({
        placeholder: 'Поиск по порталу',
        isOpened: false,
        query: '',
        isFocused: false,
        searchIndexes: [] as SearchIndexModel[],
        typeId: null as string | null,
        hints: [] as string[],
    }));
    const ref = useRef(null);
    const navigate = useNavigate();

    useOnClickOutside(ref, () => {
        store.set('isOpened', false);
    });

    const handleSearchHints = useDebounce(async () => {
        if (!store.query || store.query.length < 3) {
            store.set('searchIndexes', []);
            return;
        }
        const { isSuccess, data } = await searchHintsQuery({ query: store.query });
        if (isSuccess && data) {
            store.set('searchIndexes', data.items);
        }
    }, 1000);

    const handleSubmit = () => {
        if (!store.query) {
            return;
        }
        navigate(ROUTES.SEARCH(), {
            query: store.query,
            typeId: store.typeId,
        });
        store.set('isOpened', false);
        store.set('query', '');
        store.set('typeId', null);
    };

    const classNames = classnames('layout-header-search', {
        'layout-header-search--opened': store.isOpened,
        'layout-header-search--empty': !!store.query,
        'layout-header-search--active': store.isFocused || store.isOpened || !!store.query,
    });

    return (
        <div className={classNames} ref={ref}>
            <div className='layout-header-search__inner'>
                <div className='layout-header-search__icon'>
                    <UiIcon.Search size={24} />
                </div>
                <input
                    onKeyDown={e => (e.key === 'Enter' ? handleSubmit() : null)}
                    className='layout-header-search__control'
                    placeholder={store.placeholder}
                    onChange={e => {
                        store.set('query', e.target.value || '');
                        handleSearchHints();
                    }}
                    onFocus={() => {
                        store.set('placeholder', 'Введите запрос');
                        store.set('isFocused', true);
                    }}
                    onBlur={() => {
                        store.set('placeholder', 'Поиск по порталу');
                        setTimeout(() => store.set('isFocused', false), 200);
                    }}
                    value={store.query}
                />
                <div className={'layout-header-search__actions'}>
                    <div className='layout-header-search__clear' onClick={() => store.set('query', '')}>
                        <UiIcon.X size={12} />
                    </div>
                    <div className='layout-header-search__filter' onClick={() => store.set('isOpened', !store.isOpened)}>
                        <UiIcon.SlidersHorizontal size={20} />
                    </div>
                    <UiButton template={'small'} isDisabled={!store.query} onClick={handleSubmit}>
                        Найти
                    </UiButton>
                </div>
                {store.isFocused && !store.isOpened && !!store.searchIndexes.length && (
                    <div className='layout-header-search-hints'>
                        {store.searchIndexes.map(searchIndex => (
                            <UiNavLink key={searchIndex.id} to={ROUTES.SEARCH(`?query=${searchIndex.name}`)} className='layout-header-search-hints__item'>
                                <UiIcon.Search size={20} color={COLORS._9EA2AA} />
                                <span>{searchIndex.name}</span>
                            </UiNavLink>
                        ))}
                    </div>
                )}
                <div className='layout-header-search-filter'>
                    {SEARCH_TYPES.map(type => (
                        <UiSwitch
                            onChange={({ value }) => {
                                store.set('typeId', value ? type.id : null);
                            }}
                            value={store.typeId === type.id}
                        >
                            {type.name}
                        </UiSwitch>
                    ))}
                    <div className='layout-header-search-filter__close' onClick={() => store.set('isOpened', false)}>
                        <UiIcon.X size={24} />
                    </div>
                </div>
            </div>
        </div>
    );
});
